/**
 * EasterEggs — Phase 6.B
 *
 * Inventory of every easter egg shipped to thekitchen.cloud, with
 * status (live / expired), expiry timer, deploy commit, dismissal
 * mechanism, and (when wired) telemetry counts.
 *
 * Telemetry note: the user app doesn't currently fire events to a
 * console-readable log. The "wire telemetry" guide at the bottom of
 * the tab shows the exact 3-line snippet to drop into each egg's
 * dismissal handler so views/dismissals/clicks land in
 * R2:_console/easter-egg-events.jsonl. Until that's wired, the
 * counts column reads `—`.
 */
import { html, type TemplateResult } from "lit-html";
import { store } from "../data/store";
import { api } from "../data/api";
import type { EasterEggEventsAgg, ActiveEgg, EggRenderConfig } from "../data/types";
import { DEFAULT_RENDER } from "../data/types";
import { renderExplainButton } from "./ExplainButton";

interface EasterEgg {
  id:           string;
  name:         string;
  kind:         "static" | "overlay" | "konami" | "hidden_route";
  description:  string;
  deploy_sha:   string;
  deploy_date:  string;
  expires_at?:  string;
  storage_key?: string;
  dismissal?:   string;
  trigger?:     string;
  telemetry_id?: string;
}

// Hand-curated inventory. Update when shipping a new egg.
const EGGS: EasterEgg[] = [
  {
    id: "kitchen-easter-egg-chef",
    name: "Chef figure (bottom-right)",
    kind: "static",
    description:
      "Tiny chef PNG tucked at the bottom of every page. Fades from 0.4 → 1.0 opacity on hover. Cosmetic — no interaction beyond the hover lift.",
    deploy_sha: "—",
    deploy_date: "before EGG-2026-05-09 (predates explicit egg tracking)",
    trigger: "always rendered as <img class=\"kitchen-easter-egg\">",
    telemetry_id: "egg.chef.hover",
  },
  {
    id: "kitchen-egg-stpat-2026",
    name: "St. Patrick's slide-up overlay",
    kind: "overlay",
    description:
      "Transparent-PNG cutout that slides up from the bottom on first interaction-eligible load. Rubber-band drag dismissal (≥110px or velocity ≥0.5 px/ms), or tap-outside.",
    deploy_sha: "a9f6bbe",
    deploy_date: "2026-05-09 (EGG-2026-05-09.B)",
    expires_at: "2026-05-10T02:02:03Z",
    storage_key: "kitchen-egg-stpat-2026-seen-v2",
    dismissal: "swipe-down OR tap-outside; persists in localStorage",
    trigger: "first .ready load before EGG_EXPIRES_AT, no prior storage flag",
    telemetry_id: "egg.stpat-2026.dismiss",
  },
];

export function renderEasterEggs(onChange: () => void): TemplateResult {
  const now = Date.now();
  const evs = store.get<EasterEggEventsAgg>("eggEvents");
  const active = store.get<ActiveEgg[]>("activeEggs");
  const counts = evs.data?.counts ?? {};

  return html`
    <div class="eggs">
      ${headerCard(evs.data, active.data ?? [])}

      ${eggStudio(onChange)}

      ${activeEggsSection(active.data ?? [], onChange)}

      <section class="panel eggs-list">
        <header class="eggs-list__head">
          <h3>Static inventory · ${EGGS.length}</h3>
          <span class="eggs-list__sub">
            Cosmetic / non-deployable eggs (chef figure etc). Dynamically-deployed
            eggs are above.
          </span>
        </header>

        <ol class="eggs-list__items">
          ${EGGS.map((e) => eggCard(e, now, counts[e.telemetry_id ?? ""] ?? counts[e.id] ?? {}))}
        </ol>
      </section>

      ${recentEventsTable(evs.data)}
    </div>
  `;
}

// =============================================================
// Header
// =============================================================
function headerCard(evs: EasterEggEventsAgg | null, active: ActiveEgg[]): TemplateResult {
  const now = Date.now();
  const liveStatic = EGGS.filter((e) =>
    !e.expires_at || Date.parse(e.expires_at) > now
  ).length;
  const liveDynamic = active.filter((e) => Date.parse(e.expires_at) > now).length;
  const totalLive = liveStatic + liveDynamic;

  const totalEvents = evs?.total ?? 0;

  return html`
    <section class="panel eggs-hero">
      <div class="eggs-hero__row">
        <div class="eggs-hero__metric">
          <span class="eggs-hero__label">live eggs</span>
          <span class="eggs-hero__value">${totalLive}</span>
          <span class="eggs-hero__sub">${liveDynamic} dynamic · ${liveStatic} static</span>
        </div>
        <div class="eggs-hero__metric">
          <span class="eggs-hero__label">telemetry events (30d)</span>
          <span class="eggs-hero__value">${totalEvents}</span>
          <span class="eggs-hero__sub">
            ${totalEvents === 0 ? "none yet" : "via /api/log-easter-egg"}
          </span>
        </div>
        <span class="eggs-hero__spacer"></span>
        ${renderExplainButton({
          label: "Explain easter egg state",
          context: () => buildExplainContext(),
        })}
      </div>
    </section>
  `;
}

// =============================================================
// Per-egg card
// =============================================================
function eggCard(e: EasterEgg, now: number, kindCounts: Record<string, number>): TemplateResult {
  const isExpired = e.expires_at ? Date.parse(e.expires_at) <= now : false;
  const expiryMs = e.expires_at ? Date.parse(e.expires_at) - now : null;
  const expiryStr = expiryMs == null ? "never expires"
    : expiryMs <= 0
      ? "expired " + humanAgo(-expiryMs)
      : "expires in " + humanAgo(expiryMs);

  const kindColor = {
    static:        "var(--accent-blue-light)",
    overlay:       "var(--accent-purple-light)",
    konami:        "var(--accent-amber)",
    hidden_route:  "var(--accent-green-light)",
  }[e.kind];

  return html`
    <li class="eggs-card ${isExpired ? "is-expired" : ""}">
      <header class="eggs-card__head">
        <span class="eggs-card__kind"
              style="color:${kindColor}; border-color:${kindColor}">
          ${e.kind}
        </span>
        <h4>${e.name}</h4>
        <span class="eggs-card__spacer"></span>
        <span class="eggs-card__expiry ${isExpired ? "is-expired" : ""}">
          ${expiryStr}
        </span>
      </header>

      <p class="eggs-card__desc">${e.description}</p>

      <dl class="eggs-card__meta">
        ${e.trigger ? html`
          <dt>trigger</dt><dd>${e.trigger}</dd>
        ` : ""}
        ${e.dismissal ? html`
          <dt>dismissal</dt><dd>${e.dismissal}</dd>
        ` : ""}
        ${e.storage_key ? html`
          <dt>storage</dt><dd><code>${e.storage_key}</code></dd>
        ` : ""}
        ${e.expires_at ? html`
          <dt>expires</dt><dd>${e.expires_at}</dd>
        ` : ""}
        <dt>deploy</dt>
        <dd>
          ${e.deploy_sha === "—" ? "(predates tracking)" : html`
            <code>${e.deploy_sha}</code> · ${e.deploy_date}
          `}
        </dd>
        ${e.telemetry_id ? html`
          <dt>telemetry id</dt><dd><code>${e.telemetry_id}</code></dd>
        ` : ""}
      </dl>

      <footer class="eggs-card__foot">
        <span class="eggs-card__counts">
          ${Object.keys(kindCounts).length === 0 ? html`
            <em style="color:var(--text-quaternary);">no events yet — ensure the wire-snippet is in the user app</em>
          ` : Object.entries(kindCounts).map(([k, n]) => html`
            <span style="margin-right:14px;">${k} <strong>${n}</strong></span>
          `)}
        </span>
        ${renderExplainButton({
          label: "Explain this egg",
          context: () => buildPerEggExplainContext(e, kindCounts),
        })}
      </footer>
    </li>
  `;
}

// =============================================================
// Recent events table
// =============================================================
function recentEventsTable(evs: EasterEggEventsAgg | null): TemplateResult {
  if (!evs || evs.recent.length === 0) return html``;
  return html`
    <section class="panel eggs-recent">
      <header class="eggs-recent__head">
        <h3>Recent events · ${evs.recent.length}</h3>
        <span class="eggs-recent__sub">most recent ${evs.recent.length} from R2 log</span>
      </header>
      <ol class="eggs-recent__list">
        ${evs.recent.map((ev) => html`
          <li class="eggs-recent__row">
            <span class="eggs-recent__ts">
              ${new Date(ev.ts).toLocaleString([], {
                month: "short", day: "numeric",
                hour: "2-digit", minute: "2-digit",
              })}
            </span>
            <code class="eggs-recent__id">${ev.egg_id}</code>
            <span class="eggs-recent__kind">${ev.kind}</span>
            <span class="eggs-recent__viewport">${ev.viewport ?? "—"}</span>
            <span class="eggs-recent__ua" title=${ev.ua ?? ""}>${(ev.ua ?? "").slice(0, 40)}…</span>
          </li>
        `)}
      </ol>
    </section>
  `;
}

// =============================================================
// Egg Studio — author / preview / deploy
// =============================================================
interface StudioState {
  fileName:    string | null;
  imageBlobUrl: string | null;       // local preview src
  imageB64:    string | null;        // payload for upload
  imageBytes:  number;
  id:          string;
  name:        string;
  expiresAt:   string;               // datetime-local string
  uploading:   boolean;
  compressing: boolean;              // true while auto-fit re-encoding is running
  compressedFrom: number | null;     // original size in bytes if we compressed
  compressionScale: number;          // 1.0 = no resize; <1 = how much we shrunk dims
  uploadError: Error | null;
  render:      Required<EggRenderConfig>;
  previewKey:  number;               // bumped to recreate preview
  previewMounted: boolean;           // true while the preview egg is on-screen
}
const studio: StudioState = {
  fileName:     null,
  imageBlobUrl: null,
  imageB64:     null,
  imageBytes:   0,
  id:           "",
  name:         "",
  expiresAt:    defaultExpiry(),     // +24h from now
  uploading:    false,
  compressing:  false,
  compressedFrom: null,
  compressionScale: 1.0,
  uploadError:  null,
  render:       { ...DEFAULT_RENDER },
  previewKey:   0,
  previewMounted: false,
};

// Egg PNG cap on the worker side (mirror of EGG_MAX_BYTES in
// console-worker.ts). Server will reject above this; we auto-fit on
// the client so users don't see the rejection.
const EGG_MAX_BYTES = 1_572_864;

/**
 * Auto-fit a PNG to the egg upload cap. Loads via Canvas, then walks
 * progressively smaller scale factors re-encoding as PNG until the
 * blob fits under capBytes. Throws if even 25% scale doesn't fit
 * (vanishingly rare for typical screenshots — images bigger than
 * ~24 MB at full color depth).
 *
 * Why dimension-scaling and not quality-tuning: PNG is lossless, so
 * canvas.toBlob('image/png') has no quality knob — the only lever is
 * pixel count. For Egg Studio (slide-up images on phone screens),
 * dimension reduction is exactly the right axis: a 4000x4000 PNG
 * scaled to 1500x1500 still looks great in the iPhone-frame preview
 * but encodes at ~14% the byte size.
 */
async function compressPngToFit(
  file: File, capBytes: number,
): Promise<{ blob: Blob; scale: number }> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload  = () => resolve(el);
      el.onerror = () => reject(new Error("could not decode PNG"));
      el.src = url;
    });
    // Try at full size first (in case the user passed an already-fit
    // image but the file was just under the cap), then progressively
    // smaller. Six steps cover the typical 1.5x → 24x oversize range.
    const scales = [1.0, 0.85, 0.70, 0.55, 0.40, 0.25];
    for (const s of scales) {
      const canvas = document.createElement("canvas");
      canvas.width  = Math.max(1, Math.round(img.naturalWidth  * s));
      canvas.height = Math.max(1, Math.round(img.naturalHeight * s));
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("canvas 2d unavailable");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const blob: Blob | null = await new Promise((r) =>
        canvas.toBlob(r, "image/png")
      );
      if (!blob) continue;
      if (blob.size <= capBytes) return { blob, scale: s };
    }
    throw new Error(
      "image too large even at 25% scale — re-export at lower " +
      "color depth (8-bit indexed PNG) or use a smaller source"
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

function defaultExpiry(): string {
  const d = new Date(Date.now() + 24 * 3600_000);
  // datetime-local needs YYYY-MM-DDTHH:MM in LOCAL time
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
         `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function eggStudio(onChange: () => void): TemplateResult {
  const ready = studio.imageB64 && studio.id && studio.name && studio.expiresAt && !studio.uploading;
  const expiryHours = (() => {
    if (!studio.expiresAt) return null;
    const ms = new Date(studio.expiresAt).getTime() - Date.now();
    return ms > 0 ? (ms / 3600_000).toFixed(1) : null;
  })();

  return html`
    <section class="panel eggs-studio">
      <header class="eggs-studio__head">
        <h3>Egg Studio</h3>
        <span class="eggs-studio__sub">
          Upload PNG · preview in iPhone 17 Pro frame · set expiry · deploy.
          Live for users within seconds — no code edit, no build.
        </span>
      </header>

      <div class="eggs-studio__layout">
        <div class="eggs-studio__form">
          ${dropZone(onChange)}

          ${studio.imageB64 ? html`
            <div class="eggs-studio__field">
              <label>egg id</label>
              <input class="eggs-studio__input"
                     type="text"
                     placeholder="e.g. egg.summer-2026"
                     pattern="[\\w.-]{3,80}"
                     .value=${studio.id}
                     @input=${(e: Event) => {
                       studio.id = (e.target as HTMLInputElement).value;
                       onChange();
                     }}/>
              <span class="eggs-studio__hint">
                3-80 chars · letters/digits/<code>._-</code> only · used as
                <code>localStorage</code> key + telemetry id
              </span>
            </div>

            <div class="eggs-studio__field">
              <label>display name</label>
              <input class="eggs-studio__input"
                     type="text"
                     placeholder="e.g. Summer launch teaser"
                     .value=${studio.name}
                     @input=${(e: Event) => {
                       studio.name = (e.target as HTMLInputElement).value;
                       onChange();
                     }}/>
            </div>

            <div class="eggs-studio__field">
              <label>expires at (local time)</label>
              <input class="eggs-studio__input"
                     type="datetime-local"
                     .value=${studio.expiresAt}
                     @change=${(e: Event) => {
                       studio.expiresAt = (e.target as HTMLInputElement).value;
                       onChange();
                     }}/>
              <span class="eggs-studio__hint">
                ${expiryHours
                  ? `${expiryHours}h from now`
                  : "(must be in the future)"}
              </span>
            </div>

            ${tuningControls(onChange)}

            <div class="eggs-studio__actions">
              <button class="mp-btn mp-btn--primary"
                      ?disabled=${!ready}
                      @click=${() => deployEgg(onChange)}>
                ${studio.uploading ? "deploying…" : "deploy egg"}
              </button>
              <button class="eggs-studio__retry"
                      ?disabled=${studio.uploading}
                      @click=${() => clearStudio(onChange)}
                      title="Reset the form">
                clear
              </button>
              <button class="eggs-studio__retry"
                      ?disabled=${studio.uploading}
                      @click=${() => { studio.render = { ...DEFAULT_RENDER }; onChange(); }}
                      title="Reset positioning + animation to defaults">
                reset tuning
              </button>
            </div>
            ${studio.uploadError ? html`
              <div class="eggs-studio__err">${studio.uploadError.message}</div>
            ` : ""}
          ` : ""}
        </div>

        <div class="eggs-studio__preview">
          <header class="eggs-studio__preview-head">
            <span>Preview · iPhone 17 Pro</span>
            <button class="eggs-studio__replay"
                    ?disabled=${!studio.imageBlobUrl}
                    @click=${() => { studio.previewKey++; onChange(); }}
                    title="Replay slide-up animation">
              ↻ replay
            </button>
          </header>
          ${previewFrame()}
        </div>
      </div>
    </section>
  `;
}

function dropZone(onChange: () => void): TemplateResult {
  return html`
    <div class="eggs-studio__drop ${studio.imageBlobUrl ? "is-loaded" : ""}"
         @dragover=${(e: DragEvent) => {
           e.preventDefault();
           (e.currentTarget as HTMLElement).classList.add("is-over");
         }}
         @dragleave=${(e: DragEvent) => {
           (e.currentTarget as HTMLElement).classList.remove("is-over");
         }}
         @drop=${(e: DragEvent) => {
           e.preventDefault();
           (e.currentTarget as HTMLElement).classList.remove("is-over");
           const f = e.dataTransfer?.files?.[0];
           if (f) loadFile(f, onChange);
         }}>
      ${studio.compressing ? html`
        <div class="eggs-studio__drop-loaded">
          <span class="eggs-studio__drop-name">
            auto-fitting ${studio.fileName}…
          </span>
          <span class="eggs-studio__drop-size">
            ${studio.compressedFrom
                ? `${(studio.compressedFrom / 1024 / 1024).toFixed(2)}MB → ≤1.5MB`
                : "compressing"}
          </span>
        </div>
      ` : studio.imageBlobUrl ? html`
        <div class="eggs-studio__drop-loaded">
          <span class="eggs-studio__drop-name">${studio.fileName}</span>
          <span class="eggs-studio__drop-size">
            ${(studio.imageBytes / 1024).toFixed(1)}KB
            ${studio.compressedFrom ? html`
              <span class="eggs-studio__autofit-pill"
                     title="Auto-fit reduced this PNG by re-encoding at ${Math.round(studio.compressionScale * 100)}% of original dimensions to fit the 1.5MB cap.">
                auto-fit from ${(studio.compressedFrom / 1024 / 1024).toFixed(2)}MB
                · ${Math.round(studio.compressionScale * 100)}% scale
              </span>
            ` : ""}
          </span>
        </div>
      ` : html`
        <div class="eggs-studio__drop-empty">
          <p>Drop a transparent PNG here</p>
          <p class="eggs-studio__drop-hint">
            or <label class="eggs-studio__drop-pick">pick a file
              <input type="file" accept="image/png" hidden
                     @change=${(e: Event) => {
                       const f = (e.target as HTMLInputElement).files?.[0];
                       if (f) loadFile(f, onChange);
                     }}/>
            </label>
            · target 1.5MB (oversize PNGs auto-fit)
          </p>
        </div>
      `}
    </div>
  `;
}

// =============================================================
// Tuning controls — live sliders that re-target the preview egg
// =============================================================
function tuningControls(onChange: () => void): TemplateResult {
  const r = studio.render;
  const setProp = (k: keyof EggRenderConfig, v: number) => {
    (studio.render as any)[k] = v;
    onChange();
  };
  return html`
    <details class="eggs-studio__tuning" open>
      <summary>positioning + animation tuning</summary>
      <div class="eggs-studio__tuning-grid">
        ${slider("height %",   "height_pct",   20,  100, 1, r.height_pct,   "of phone height", setProp)}
        ${slider("width %",    "width_pct",    20,  100, 1, r.width_pct,    "max-width cap",   setProp)}
        ${slider("x offset",   "x_offset_pct",-25,   25, 1, r.x_offset_pct, "% from center",   setProp)}
        ${slider("y offset",   "y_offset_px", -100, 200, 1, r.y_offset_px,  "px from bottom",  setProp)}
        ${slider("duration",   "anim_ms",     150, 1500, 10, r.anim_ms,     "slide-up ms",      setProp)}
        ${slider("delay",      "delay_ms",      0, 1500, 10, r.delay_ms,    "ms before start",  setProp)}
      </div>
    </details>
  `;
}
function slider(
  label: string,
  key: keyof EggRenderConfig,
  lo: number, hi: number, step: number,
  value: number,
  hint: string,
  set: (k: keyof EggRenderConfig, v: number) => void,
): TemplateResult {
  return html`
    <label class="eggs-studio__slider">
      <span class="eggs-studio__slider-label">${label}</span>
      <input type="range" min=${lo} max=${hi} step=${step}
             .value=${String(value)}
             @input=${(e: Event) => set(key, Number((e.target as HTMLInputElement).value))}/>
      <input type="number" min=${lo} max=${hi} step=${step}
             class="eggs-studio__slider-num"
             .value=${String(value)}
             @input=${(e: Event) => set(key, Number((e.target as HTMLInputElement).value))}/>
      <span class="eggs-studio__slider-hint">${hint}</span>
    </label>
  `;
}

// =============================================================
// Interactive preview — uses the same drag-to-dismiss handler as
// production. Drag the image down, swipe-flick, or tap outside to
// dismiss. Replay button mounts a fresh element + restarts the
// animation. Production-equivalent behavior.
// =============================================================
function previewFrame(): TemplateResult {
  const PREVIEW_W = 402;
  const PREVIEW_H = 874;
  const scale     = 0.55;
  const renderedW = Math.round(PREVIEW_W * scale);
  const renderedH = Math.round(PREVIEW_H * scale);
  const r = studio.render;

  // Build inline custom-prop styles same as production
  const eggStyle = `
    --egg-height-pct: ${r.height_pct}%;
    --egg-width-pct:  ${r.width_pct}%;
    --egg-x-offset-pct: ${r.x_offset_pct};
    --egg-y-offset-px:  ${r.y_offset_px}px;
    --egg-anim-ms:      ${r.anim_ms}ms;
    --egg-delay-ms:     ${r.delay_ms}ms;
  `;

  // CAL-2026-05-10 BUGFIX: previously this fired via
  //   ${attachPreviewMount(eggStyle)}
  // in element-attribute position, but bare ${expr} between
  // attributes isn't a valid lit-html pattern (it accepts only
  // directives / property / event / boolean-attr bindings there) —
  // the function was only invoked on first render, never on slider
  // drags. Call queueMicrotask directly from the render path so the
  // mount/restyle hook runs after every paint regardless of how
  // lit-html handles the in-template expression.
  if (studio.imageBlobUrl) {
    queueMicrotask(() => mountPreviewEgg(eggStyle));
  }

  return html`
    <div class="eggs-studio__phone"
         style="width:${renderedW}px;height:${renderedH}px;">
      <div class="eggs-studio__phone-inner"
           style="width:${PREVIEW_W}px;height:${PREVIEW_H}px;
                   transform:scale(${scale}); transform-origin: top left;">
        <div class="eggs-studio__phone-notch"></div>
        <div class="eggs-studio__phone-bg">
          <span>kitchen.cloud</span>
        </div>
        ${studio.imageBlobUrl
          ? html`<div class="eggs-studio__phone-screen"
                       data-replay-key=${studio.previewKey}></div>`
          : html`<div class="eggs-studio__phone-empty">
                   Preview appears here once you drop a PNG.
                 </div>`}
      </div>
    </div>
  `;
}

let lastMountedKey = -1;
function mountPreviewEgg(eggStyle: string): void {
  const screen = document.querySelector(".eggs-studio__phone-screen") as HTMLDivElement | null;
  if (!screen) {
    studio.previewMounted = false;
    return;
  }
  const currentKey = Number(screen.dataset.replayKey ?? "0");

  // Sliders should LIVE-UPDATE the existing preview without remounting.
  // Only do a fresh mount when the previewKey has actually bumped
  // (replay button or new file dropped).
  const existingOverlay = screen.querySelector(".eggs-studio__egg-overlay") as HTMLDivElement | null;
  if (existingOverlay && currentKey === lastMountedKey) {
    existingOverlay.setAttribute("style", eggStyle);
    return;
  }
  lastMountedKey = currentKey;

  // Fresh mount — wipe any previous overlay
  screen.innerHTML = "";

  const overlay = document.createElement("div");
  overlay.className = "eggs-studio__egg-overlay";
  overlay.setAttribute("style", eggStyle);

  const img = document.createElement("img");
  img.className = "eggs-studio__egg-img";
  img.alt = "";
  img.draggable = false;
  // Hide visually until the image has loaded so we don't flash a
  // half-decoded frame; trigger the slide-up animation only once
  // intrinsic dimensions are known. This eliminates the "smaller on
  // replay" artifact where the animation ran against a 0×0 image
  // and CSS max-height/max-width resolved oddly.
  img.style.visibility = "hidden";
  img.addEventListener("load", () => {
    img.style.visibility = "";
    void overlay.offsetHeight;        // force layout
    overlay.classList.add("is-visible");
  }, { once: true });
  // Set src LAST so the load event handler is wired before decode
  img.src = studio.imageBlobUrl!;
  overlay.appendChild(img);
  screen.appendChild(overlay);

  studio.previewMounted = true;

  // ---- production drag-to-dismiss logic ----
  const DISMISS_DRAG_PX = 110;
  const DISMISS_VEL     = 0.5;
  let dragging = false, startY = 0, lastY = 0, lastT = 0,
      velocity = 0, currentDelta = 0, dismissed = false;

  function dismiss() {
    if (dismissed) return;
    dismissed = true;
    overlay.classList.remove("is-visible");
    overlay.classList.add("is-dismissing");
    img.style.transform = "translate3d(calc(-50% + var(--egg-x-offset-pct, 0) * 1%), 100%, 0)";
    setTimeout(() => {
      overlay.style.display = "none";
      studio.previewMounted = false;
    }, 600);
  }
  function onDown(e: PointerEvent) {
    if (dismissed || (e.button !== undefined && e.button !== 0)) return;
    dragging = true;
    startY = lastY = e.clientY;
    lastT = performance.now();
    velocity = 0;
    currentDelta = 0;
    overlay.classList.add("is-dragging");
    img.setPointerCapture?.(e.pointerId);
  }
  function onMove(e: PointerEvent) {
    if (!dragging || dismissed) return;
    const now = performance.now();
    const y   = e.clientY;
    const dt  = Math.max(1, now - lastT);
    velocity  = (y - lastY) / dt;
    lastY     = y;
    lastT     = now;
    let delta = Math.max(0, y - startY);
    // The preview phone is scaled 0.55× so user pixel deltas map to
    // 1/0.55 ≈ 1.82× egg-space pixels. Compensate so dismiss feels
    // correct in preview.
    delta = delta / 0.55;
    if (delta > 874 * 0.6) {
      delta = 874 * 0.6 + (delta - 874 * 0.6) * 0.4;
    }
    currentDelta = delta;
    img.style.transform = `translate3d(calc(-50% + var(--egg-x-offset-pct, 0) * 1%), ${delta}px, 0)`;
  }
  function onUp() {
    if (!dragging) return;
    dragging = false;
    overlay.classList.remove("is-dragging");
    if (currentDelta >= DISMISS_DRAG_PX || velocity >= DISMISS_VEL) {
      dismiss();
    } else {
      img.style.transform = "";   // restore default rest position via CSS
    }
  }
  function onScreenClick(e: MouseEvent) {
    if (e.target === img) return;
    if (currentDelta > 4) return;
    dismiss();
  }
  img.addEventListener("pointerdown",   onDown);
  img.addEventListener("pointermove",   onMove);
  img.addEventListener("pointerup",     onUp);
  img.addEventListener("pointercancel", onUp);
  screen.addEventListener("click", onScreenClick);
  img.addEventListener("dragstart", (e) => e.preventDefault());
}

function activeEggsSection(active: ActiveEgg[], onChange: () => void): TemplateResult {
  if (active.length === 0) {
    return html`
      <section class="panel eggs-active eggs-active--empty">
        <h3>Live eggs · 0</h3>
        <p>No dynamic eggs deployed. Use the Studio above to ship one.</p>
      </section>
    `;
  }
  return html`
    <section class="panel panel--hero eggs-active">
      <header class="eggs-active__head">
        <h3>Live eggs · ${active.length}</h3>
        <span class="eggs-active__sub">
          fetched from
          <code>/api/active-eggs</code> · user app picks these up on next page load
        </span>
      </header>
      <ol class="eggs-active__list">
        ${active.map((e) => activeEggRow(e, onChange))}
      </ol>
    </section>
  `;
}

function activeEggRow(e: ActiveEgg, onChange: () => void): TemplateResult {
  const expiresMs = Date.parse(e.expires_at) - Date.now();
  const expired = expiresMs <= 0;
  const expiryStr = expired
    ? "expired"
    : expiresMs < 3600_000
      ? `${Math.round(expiresMs / 60_000)}m left`
      : `${(expiresMs / 3600_000).toFixed(1)}h left`;
  return html`
    <li class="eggs-active-row">
      <code class="eggs-active-row__id">${e.id}</code>
      <span class="eggs-active-row__name">${e.name}</span>
      <span class="eggs-active-row__expiry ${expired ? "is-expired" : ""}">${expiryStr}</span>
      <span class="eggs-active-row__deployed-by">
        by ${e.deployed_by ?? "—"}
      </span>
      <button class="eggs-active-row__retract"
              @click=${() => retractEgg(e.id, onChange)}>
        retract
      </button>
    </li>
  `;
}

// =============================================================
// File handling + deploy
// =============================================================
async function loadFile(file: File, onChange: () => void): Promise<void> {
  if (file.type !== "image/png") {
    studio.uploadError = new Error("only PNG supported in v1");
    onChange();
    return;
  }

  // Decide whether we need to auto-fit. Files at-or-under cap go
  // through the fast path unchanged; oversize files get re-encoded
  // via canvas at progressively smaller scales.
  let workingBlob: Blob = file;
  let originalSize: number | null = null;
  let scale = 1.0;
  if (file.size > EGG_MAX_BYTES) {
    studio.compressing  = true;
    studio.uploadError  = null;
    studio.fileName     = file.name;            // show filename while compressing
    studio.compressedFrom = file.size;
    onChange();
    try {
      const result = await compressPngToFit(file, EGG_MAX_BYTES);
      workingBlob  = result.blob;
      originalSize = file.size;
      scale        = result.scale;
    } catch (e) {
      studio.compressing   = false;
      studio.compressedFrom = null;
      studio.uploadError   = e instanceof Error ? e : new Error(String(e));
      onChange();
      return;
    }
    studio.compressing = false;
  }

  // Common path — once we have a fitting blob (either original or
  // post-compression), wire up preview + base64 payload.
  studio.uploadError      = null;
  studio.fileName         = file.name;
  studio.imageBytes       = workingBlob.size;
  studio.compressedFrom   = originalSize;
  studio.compressionScale = scale;
  if (studio.imageBlobUrl) URL.revokeObjectURL(studio.imageBlobUrl);
  studio.imageBlobUrl     = URL.createObjectURL(workingBlob);

  // Read base64 from the (possibly compressed) blob, NOT the original
  const reader = new FileReader();
  reader.onload = () => {
    const dataUrl = reader.result as string;       // "data:image/png;base64,xxxxx"
    const b64 = dataUrl.split(",")[1] ?? "";
    studio.imageB64 = b64;
    // Default id from filename if empty
    if (!studio.id) {
      studio.id = "egg." + file.name.replace(/\.png$/i, "")
                                   .toLowerCase()
                                   .replace(/[^\w]/g, "-")
                                   .replace(/-+/g, "-")
                                   .slice(0, 60);
    }
    if (!studio.name) {
      studio.name = file.name.replace(/\.png$/i, "");
    }
    studio.previewKey++;     // trigger preview animation
    onChange();
  };
  reader.readAsDataURL(workingBlob);
}

function clearStudio(onChange: () => void): void {
  if (studio.imageBlobUrl) URL.revokeObjectURL(studio.imageBlobUrl);
  studio.fileName     = null;
  studio.imageBlobUrl = null;
  studio.imageB64     = null;
  studio.imageBytes   = 0;
  studio.id           = "";
  studio.name         = "";
  studio.expiresAt    = defaultExpiry();
  studio.uploading    = false;
  studio.compressing  = false;
  studio.compressedFrom = null;
  studio.compressionScale = 1.0;
  studio.uploadError  = null;
  studio.previewKey   = 0;
  onChange();
}

async function deployEgg(onChange: () => void): Promise<void> {
  if (studio.uploading || !studio.imageB64) return;
  studio.uploading = true;
  studio.uploadError = null;
  onChange();
  try {
    // Convert datetime-local (browser-local) to ISO UTC
    const iso = new Date(studio.expiresAt).toISOString();
    // Only include render keys that differ from defaults so we keep
    // the descriptor lean.
    const render: EggRenderConfig = {};
    for (const k of Object.keys(DEFAULT_RENDER) as (keyof EggRenderConfig)[]) {
      if (studio.render[k] !== DEFAULT_RENDER[k]) render[k] = studio.render[k];
    }
    await (api as any).eggUpload({
      id:         studio.id,
      name:       studio.name,
      expires_at: iso,
      animation:  "slide-up",
      image_b64:  studio.imageB64,
      ...(Object.keys(render).length > 0 ? { render } : {}),
    });
    // Success — refresh active list and clear the form
    store.refetch("activeEggs");
    clearStudio(onChange);
  } catch (e) {
    studio.uploadError = e instanceof Error ? e : new Error(String(e));
  } finally {
    studio.uploading = false;
    onChange();
  }
}

async function retractEgg(id: string, onChange: () => void): Promise<void> {
  if (!confirm(`Retract egg "${id}"? Users currently in dismissal-eligible window will stop seeing it on their next page load.`)) {
    return;
  }
  try {
    await api.eggRetract(id);
    store.refetch("activeEggs");
  } catch (e) {
    alert(`retract failed: ${e instanceof Error ? e.message : String(e)}`);
  }
  onChange();
}

// =============================================================
// Explain
// =============================================================
function buildExplainContext(): string {
  const now = Date.now();
  const lines = ["Easter egg inventory:"];
  for (const e of EGGS) {
    const live = !e.expires_at || Date.parse(e.expires_at) > now;
    lines.push(
      `  ${e.id} (${e.kind}): ${live ? "live" : "expired"}` +
      (e.expires_at ? ` until ${e.expires_at}` : ""),
    );
  }
  lines.push(
    "",
    "Telemetry: not yet wired. Snippet at the bottom of the tab " +
      "shows the 3-step path to plumb fire counts into this view.",
  );
  return lines.join("\n");
}

function buildPerEggExplainContext(e: EasterEgg, kindCounts: Record<string, number>): string {
  const lines = [
    `Easter egg: ${e.name} (${e.id})`,
    `Kind: ${e.kind}`,
    `Description: ${e.description}`,
    `Trigger: ${e.trigger ?? "—"}`,
    `Dismissal: ${e.dismissal ?? "—"}`,
    `Storage key: ${e.storage_key ?? "—"}`,
    `Expires: ${e.expires_at ?? "—"}`,
    `Deploy: ${e.deploy_sha} on ${e.deploy_date}`,
  ];
  if (Object.keys(kindCounts).length > 0) {
    lines.push("", `Telemetry counts (last 30d):`);
    for (const [k, n] of Object.entries(kindCounts)) {
      lines.push(`  ${k}: ${n}`);
    }
  }
  return lines.join("\n");
}

function humanAgo(ms: number): string {
  const abs = Math.abs(ms);
  const minutes = Math.floor(abs / 60_000);
  if (minutes < 60)         return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48)           return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}
